import { Box, Button, HStack, Icon, Stack, Text, Textarea } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCheckCircle } from "react-icons/fi";
import { type BuyerBidDetailDto } from "../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import {
  DeclaredExclusionGrounds,
  ExclusionGroundsAssessmentRecord,
} from "../qualification/detail/DeclaredExclusionGrounds";
import { formatDecidedBy } from "../qualification/detail/formatDecidedBy";
import { useBidQualificationDecision } from "./useBidQualificationDecision";

interface Props {
  detail: BuyerBidDetailDto;
  onShowEspd?: () => void;
}

/**
 * The «kvalifisert» decision for a single-stage procedure. The negative outcome is deliberately
 * absent: disqualifying a supplier is an avvisning, and lives in the rejection flow next to this.
 * Decisions freeze once the qualification is completed (the event has opened its evaluation).
 *
 * Whether the decision applies at all follows the procedure, read off the event the page has
 * already loaded — the same derivation BidDetailView does for its two-stage tabs.
 */
export const BidQualificationDecision = ({ detail, onShowEspd }: Props) => {
  const { t } = useTranslation();
  const displayDateWithTime = useDisplayDateWithTime();
  const { applies, decision, decisionOpen, reopenedAfterJuryDecision, pending, assessmentRequired, decide, isLoading } =
    useBidQualificationDecision(detail);
  const [assessment, setAssessment] = useState(detail.exclusionGroundsAssessment?.text ?? "");
  const assessmentMissing = assessmentRequired && assessment.trim() === "";
  const assessmentTooLong = assessmentRequired && assessment.trim().length > MAX_EXCLUSION_GROUNDS_ASSESSMENT_LENGTH;

  if (!applies) return null;

  const assessmentField = (
    <>
      <DeclaredExclusionGrounds grounds={detail.declaredExclusionGrounds ?? []} onShowEspd={onShowEspd} />
      <Box>
        <Text fontSize="sm" fontWeight="medium" color="smText" mb="1">
          {t("Assessment of exclusion grounds")}
        </Text>
        <Textarea
          aria-label={t("Assessment of exclusion grounds") ?? undefined}
          value={assessment}
          onChange={(e) => setAssessment(e.target.value)}
          bg="smBackgroundSecondary"
          rounded="md"
          fontSize="sm"
          mb="1"
        />
        <Text fontSize="xs" color="smMuted">
          {t(
            "Explain why the supplier can be qualified despite the declared grounds. Kept with the decision, never shared with the supplier.",
          )}
        </Text>
        <Text fontSize="xs" color={assessmentTooLong ? "smDanger" : "smMuted"} mt="1">
          {t("{{count}} / {{max}} characters", {
            count: assessment.trim().length,
            max: MAX_EXCLUSION_GROUNDS_ASSESSMENT_LENGTH,
          })}
        </Text>
      </Box>
    </>
  );
  const assessmentHint = (
    <>
      {assessmentMissing && (
        <Text fontSize="xs" color="smMuted">
          {t("Write the assessment of the exclusion grounds first")}
        </Text>
      )}
      {assessmentTooLong && (
        <Text fontSize="xs" color="smMuted">
          {t("Shorten the assessment to at most {{max}} characters", {
            max: MAX_EXCLUSION_GROUNDS_ASSESSMENT_LENGTH,
          })}
        </Text>
      )}
    </>
  );

  if (decision) {
    return (
      <Stack spacing="1">
        <HStack spacing="2" color="green.700">
          <Icon as={FiCheckCircle} />
          <Text fontSize="sm" fontWeight="semibold">
            {t("Qualified")}
          </Text>
        </HStack>
        <Text fontSize="xs" color="smMuted">
          {formatDecidedBy(decision, displayDateWithTime, t)}
        </Text>
        {detail.exclusionGroundsAssessment && (
          <Box pt="2">
            <ExclusionGroundsAssessmentRecord assessment={detail.exclusionGroundsAssessment} />
          </Box>
        )}
        {reopenedAfterJuryDecision && assessmentRequired && !detail.exclusionGroundsAssessment && (
          <Stack spacing="3" pt="3">
            <Text fontSize="sm" color="smText">
              {t(
                "Now that the jury's decision is registered, the entry's declared exclusion grounds are shown. Assess them before you choose the winners.",
              )}
            </Text>
            {assessmentField}
            <Stack spacing="1">
              <Button
                colorScheme="teal"
                width="full"
                isLoading={isLoading}
                isDisabled={assessmentMissing || assessmentTooLong}
                onClick={() => decide(true, assessment)}
              >
                {t("Save the assessment")}
              </Button>
              {assessmentHint}
            </Stack>
          </Stack>
        )}
        {decisionOpen && (
          <Box>
            <Button
              variant="link"
              size="sm"
              colorScheme="teal"
              isLoading={isLoading}
              onClick={() => decide(false)}
            >
              {t("Undo the qualification")}
            </Button>
          </Box>
        )}
      </Stack>
    );
  }

  if (!decisionOpen) return null;

  return (
    <Stack spacing="3">
      {assessmentRequired && assessmentField}
      <Stack spacing="1">
        <Button
          colorScheme="teal"
          width="full"
          isLoading={isLoading}
          isDisabled={pending > 0 || assessmentMissing || assessmentTooLong}
          onClick={() => decide(true, assessmentRequired ? assessment : undefined)}
        >
          {t("Mark as qualified")}
        </Button>
        {pending > 0 && (
          <Text fontSize="xs" color="smMuted">
            {t("Review all qualification requirements first ({{count}} remaining)", {
              count: pending,
            })}
          </Text>
        )}
        {assessmentHint}
      </Stack>
    </Stack>
  );
};

// Mirrors MAX_REVIEW_TEXT_LENGTH in the BFF, which refuses a longer assessment.
const MAX_EXCLUSION_GROUNDS_ASSESSMENT_LENGTH = 4000;
